import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/BucksLanding.css';
// Import the new cover photo
import coverPhoto from '../imgs/front_BucksCoverPhoto.jpg';
import bucksBoxImage from '../imgs/bucksBoxImage.png';
// Import the story image
import bucksStoryImage from '../imgs/bucksLandingPageOurStory_IMG.png';

function BucksLanding() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // New Product States
  const [seasonalProducts, setSeasonalProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productError, setProductError] = useState(null);
  
  // Newsletter subscription states
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);
  
  // Add these new state variables for quantity input functionality
  const [quantityInputMode, setQuantityInputMode] = useState({}); // Track which products are in input mode
  const [quantityValues, setQuantityValues] = useState({}); // Track quantity values for each product
  
  // Get the cart context functions
  const { addToCart } = useCart();

  // Sample testimonials
  const testimonials = [
    {
      quote: "The produce from Bucks Farmstand is the freshest I've ever tasted. It's changed how our family eats.",
      author: "Sarah M., Local Customer"
    },
    {
      quote: "Everyone knows your name and the produce is always picked just right.",
      author: "James T., Alsip Resident"
    },
    {
      quote: "There's something special about Buck's. It's more than fresh food, it's a place where families feel at home.",
      author: "Emma L., Family Shopper"
    }
  ];

  // Fallback seasonal products in case API fails - we'll keep the definition but won't use it
  const fallbackProducts = [
    {
      id: 'featured-1',
      name: "Fresh Apples",
      description: "Hand-picked daily, our apples are crisp and full of flavor.",
      image: "https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80",
      price: 4.99,
      category: "Fruit"
    },
    {
      id: 'featured-2',
      name: "Heirloom Tomatoes",
      description: "Bursting with flavor, our tomatoes are grown with care.",
      image: "https://images.unsplash.com/photo-1518977822534-7049a61ee0c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80",
      price: 5.99,
      category: "Vegetable"
    },
    {
      id: 'featured-3',
      name: "Seasonal Berries",
      description: "Sweet and juicy, straight from our fields to your table.",
      image: "https://images.unsplash.com/photo-1583513702439-2e611c58e93d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80",
      price: 6.99,
      category: "Fruit"
    }
  ];

  // Enhanced notification function to match shop page - DEFINED FIRST
  const showNotification = useCallback((message, type = 'success') => {
    const notification = document.getElementById('cart-notification');
    if (notification) {
      notification.textContent = message;
      notification.classList.add('show');
      if (type === 'error') {
        notification.classList.add('error');
      }
      
      setTimeout(() => {
        notification.classList.remove('show', 'error');
      }, type === 'error' ? 5000 : 3000);
    }
  }, []); // Empty dependency array - function is stable

  // Add this helper function for special pricing calculation
  const calculateSpecialPricing = useCallback((product, quantity) => {
    // Special pricing for sweet corn - dozen pricing
    if (product.name && product.name.toLowerCase().includes('corn') && quantity >= 12) {
      const dozens = Math.floor(quantity / 12);
      const remainder = quantity % 12;
      const dozenPrice = 8.00; // $8 per dozen
      const singlePrice = product.price; // Original unit price
      
      return {
        totalPrice: (dozens * dozenPrice) + (remainder * singlePrice),
        effectiveUnitPrice: (dozens * dozenPrice + remainder * singlePrice) / quantity,
        hasSpecialPricing: true,
        dozens: dozens,
        remainder: remainder
      };
    }
    
    return {
      totalPrice: product.price * quantity,
      effectiveUnitPrice: product.price,
      hasSpecialPricing: false
    };
  }, []);

  // Enhanced quantity change handler that allows empty strings temporarily
  const handleQuantityChange = useCallback((productId, value) => {
    // Allow empty string temporarily for better UX
    if (value === '') {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: ''
      }));
      return;
    }
    
    // Parse the value and ensure it's a positive number
    const numValue = parseInt(value, 10);
    
    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue > 0) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: numValue
      }));
    }
  }, []);

  // Add this new function to handle when user leaves the input field
  const handleQuantityBlur = useCallback((productId) => {
    const currentValue = quantityValues[productId];
    
    // If empty or invalid, set to 1
    if (currentValue === '' || currentValue === null || currentValue === undefined || currentValue < 1) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: 1
      }));
    }
  }, [quantityValues]);

  // Add this function to handle input focus (select all text)
  const handleQuantityFocus = useCallback((event) => {
    // Select all text when input is focused for easier editing
    event.target.select();
  }, []);

  // Add this function to cancel quantity input mode
  const handleCancelQuantityInput = useCallback((productId, event) => {
    if (event) {
      event.stopPropagation();
    }
    setQuantityInputMode(prev => ({
      ...prev,
      [productId]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [productId]: 1
    }));
  }, []);

  // Enhanced add to cart handler with proper corn identification and consistent product structure
  const handleAddToCart = useCallback((product, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // If not in quantity input mode, show the input
    if (!quantityInputMode[product.id]) {
      setQuantityInputMode(prev => ({
        ...prev,
        [product.id]: true
      }));
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1 // Default to 1
      }));
      return;
    }
    
    // Ensure we have a valid quantity before adding to cart
    let quantity = quantityValues[product.id];
    if (quantity === '' || quantity === null || quantity === undefined || quantity < 1) {
      quantity = 1;
      // Update the state to reflect the corrected value
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1
      }));
    }
    
    const specialPricing = calculateSpecialPricing(product, quantity);
    
    // Ensure consistent product structure - normalize the product
    const cartProduct = { 
      // Use a consistent ID format - ensure numeric ID
      id: typeof product.id === 'string' ? parseInt(product.id, 10) || product.id : product.id,
      // Ensure consistent name formatting
      name: product.name.trim(),
      price: product.price,
      image: product.image,
      category: product.category,
      description: product.description,
      status: product.status,
      // Cart-specific fields
      quantity: quantity,
      unit: product.unit || 'ea',
      effectiveUnitPrice: specialPricing.effectiveUnitPrice,
      originalPrice: product.price,
      hasSpecialPricing: specialPricing.hasSpecialPricing,
      specialPricingInfo: specialPricing.hasSpecialPricing ? {
        dozens: specialPricing.dozens,
        remainder: specialPricing.remainder,
        dozenPrice: 8.00,
        originalUnitPrice: product.price,
        totalSavings: (quantity * product.price) - specialPricing.totalPrice
      } : null
    };
    
    // Debug log to ensure proper data structure
    console.log('Cart product being added from landing page:', cartProduct);
    
    addToCart(cartProduct);
    
    // Enhanced notification message for special pricing
    let notificationMessage;
    if (specialPricing.hasSpecialPricing && specialPricing.dozens > 0) {
      if (specialPricing.remainder > 0) {
        notificationMessage = `${specialPricing.dozens} dozen + ${specialPricing.remainder} ${product.name} added to basket (${specialPricing.totalPrice.toFixed(2)})`;
      } else {
        notificationMessage = `${specialPricing.dozens} dozen ${product.name} added to basket (${specialPricing.totalPrice.toFixed(2)})`;
      }
    } else {
      // Updated: Include unit for lbs, omit for other units
      const unit = product.unit || 'ea';
      if (unit.toLowerCase() === 'lbs' || unit.toLowerCase() === 'lb') {
        notificationMessage = `${quantity} ${unit} of ${product.name} added to basket`;
      } else {
        notificationMessage = `${quantity} ${product.name} added to basket`;
      }
    }
    
    // Show notification using enhanced notification system
    showNotification(notificationMessage);
    
    // Reset the input mode and value
    setQuantityInputMode(prev => ({
      ...prev,
      [product.id]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [product.id]: 1
    }));
  }, [addToCart, quantityInputMode, quantityValues, calculateSpecialPricing, showNotification]);

  // Fetch announcements from API - UPDATED to not use mock data when no announcements
  useEffect(() => {
    const fetchAnnouncements = async (retryCount = 0) => {
      try {
        setIsLoading(true);
        // Add timestamp to prevent caching and add limit parameter
        const apiUrl = `https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-announcements-get?limit=5&_=${Date.now()}`;
        console.log(`Fetching announcements from: ${apiUrl}`);
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch announcements: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Announcements API response:', data);
        
        // Check if the response is valid
        if (!data) {
          throw new Error('Empty response from announcements API');
        }
        
        // If data is empty or not an array, retry or use empty announcements
        if (!Array.isArray(data) || data.length === 0) {
          if (retryCount < 2) {
            console.log(`No announcements found, retrying (${retryCount + 1}/3)...`);
            setTimeout(() => fetchAnnouncements(retryCount + 1), 1000);
            return;
          }
          console.log('No announcements returned after retries');
          // Set empty announcements array - no fallbacks
          setAnnouncements([]);
        } else {
          console.log(`Successfully fetched ${data.length} announcements`);
          setAnnouncements(data);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching announcements:", err);
        setError(err.message);
        
        // Retry logic for network errors
        if (retryCount < 2) {
          console.log(`Error fetching announcements, retrying (${retryCount + 1}/3)...`);
          setTimeout(() => fetchAnnouncements(retryCount + 1), 1500);
          return;
        }
        
        // Set empty announcements in case of error
        setAnnouncements([]);
        setIsLoading(false);
      }
    };

    fetchAnnouncements();
    
    // Refresh announcements periodically (every 5 minutes)
    const announcementRefreshInterval = setInterval(() => {
      console.log('Refreshing announcements data...');
      fetchAnnouncements();
    }, 300000); // 5 minutes in milliseconds
    
    return () => {
      clearInterval(announcementRefreshInterval);
    };
  }, []);

  // UPDATED: Fetch seasonal products from API with CORS fix
  useEffect(() => {
    const fetchSeasonalProducts = async () => {
      try {
        setIsLoadingProducts(true);
        console.log('Fetching seasonal products from API...');
        
        const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-landing-page-inventory-get', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          // Remove cache-busting headers that cause CORS preflight issues
          // mode: 'cors', // This is the default, so we can omit it
        });
        
        // Log the raw response for debugging
        console.log('API Response Status:', response.status);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch seasonal products: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Response Data:', data);
        
        // Check if the response contains products and is in the expected format
        if (data && data.products && Array.isArray(data.products) && data.products.length > 0) {
          setSeasonalProducts(data.products);
          console.log(`Successfully loaded ${data.products.length} seasonal products`);
        } else {
          // If no products are found, don't use fallback mock data
          console.log("API returned no products, showing empty state");
          setSeasonalProducts([]);
        }
      } catch (err) {
        console.error("Error fetching seasonal products:", err);
        setProductError(err.message);
        
        // Don't use fallback mock data, just set empty array
        setSeasonalProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchSeasonalProducts();
  }, []);

  // Animation on scroll and rotations for testimonials and announcements
  useEffect(() => {
    setIsVisible(true);
    
    // Testimonial rotation
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    // Announcement rotation - only start if announcements are loaded
    let announcementInterval;
    if (announcements.length > 0) {
      announcementInterval = setInterval(() => {
        setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
      }, 7000);
    }
    
    return () => {
      clearInterval(testimonialInterval);
      if (announcementInterval) clearInterval(announcementInterval);
    };
  }, [announcements.length, testimonials.length]);

  // Helper function to format date string - ENHANCED
  const formatAnnouncementDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let normalizedDateString = dateString;
      
      if (!normalizedDateString.includes('T') && !normalizedDateString.includes('Z')) {
        normalizedDateString = normalizedDateString.replace(' ', 'T');
      }
      
      const date = new Date(normalizedDateString);
      
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return '';
      }
      
      // Show relative time for recent dates
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        return 'Posted yesterday';
      } else if (diffDays < 7) {
        return `Posted ${diffDays} days ago`;
      } else {
        // Format options for older dates
        const options = { 
          month: 'long', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        };
        return `Posted ${date.toLocaleDateString('en-US', options)}`;
      }
    } catch (e) {
      console.error("Error formatting date", e, dateString);
      return '';
    }
  };

  // Handle newsletter subscription
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Reset message states
    setSubscribeMessage(null);
    setSubscribeError(null);
    
    // Validate email
    if (!email || !email.includes('@') || !email.includes('.')) {
      setSubscribeError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-email-subscriber-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name: name || undefined
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribeMessage(data.message || 'Thank you for subscribing to our newsletter!');
        setEmail(''); // Clear the email input on success
        setName(''); // Clear the name input on success
      } else {
        setSubscribeError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting newsletter form:', err);
      setSubscribeError('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to navigate to shop page
  const navigateToShop = () => {
    window.location.href = '/shop';
  };

  // Navigate to Buck's Box page
  const navigateToSubscription = () => {
    window.location.href = '/bucks-box';
  };

  // Navigate to About/Story page
  const navigateToStory = () => {
    window.location.href = '/about';
  };

  // Debug function to check announcement data - remove in production
  const debugAnnouncements = () => {
    if (announcements.length > 0) {
      console.log('Current announcement:', announcements[currentAnnouncement]);
    }
  };

  // Call debug once on initial load
  useEffect(() => {
    if (announcements.length > 0 && !isLoading) {
      debugAnnouncements();
    }
  }, [announcements, isLoading]);

  return (
    <div className="bucks-landing">
      {/* Enhanced Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-content">
          <h1>Taste Nature's Perfect Harvest, Delivered</h1>
          <p className="hero-subtitle">
            Experience the difference of locally grown, seasonal produce straight from our fertile land to your table — always fresh, always sustainable.
          </p>
          <div className="hero-cta-container">
            <button className="cta-primary" onClick={navigateToShop}>
              Explore the Harvest
            </button>
            <button className="cta-secondary" onClick={() => window.location.href = '/about'}>
              Our Story
            </button>
          </div>
          
          {/* Subtle testimonial preview - Ogilvy's social proof principle */}
          <div className="hero-testimonial-preview">
            <div className="rating-stars">★★★★★</div>
            <p>"The freshest produce I've ever tasted..."</p>
          </div>
        </div>
        
        <div className="hero-image-container">
          <div className="hero-decoration-circle"></div>
          <img
            src={coverPhoto}
            alt="Fresh seasonal produce from Buck's Farmstand"
            className="hero-image"
          />
          <div className="hero-image-badge">
            <span>100%</span>
            <span>Local</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2>Why Choose Bucks Farmstand?</h2>
        <div className="benefits-container">
          <div className="benefit-card">
            <div className="benefit-icon">🌱</div>
            <h3>Always Fresh</h3>
            <p>Harvested daily, our produce reaches you at peak freshness and flavor.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🚜</div>
            <h3>Locally Grown</h3>
            <p>Support local agriculture and reduce environmental impact with our farm-to-table approach.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🧺</div>
            <h3>Farm-Direct Experience</h3>
            <p>Our farmstand offers a genuine connection to your food, with hand-selected produce straight from local fields to your table.</p>
          </div>
        </div>
      </section>

      {/* Announcement Board Section */}
      <section className="announcement-section">
        <h2>From the Buck's Board</h2>
        <p className="announcement-subtitle">
          Stay updated with what's happening at our farmstand
        </p>
        <div className="announcement-board">
          {isLoading ? (
            <div className="announcement-loading">Loading announcements...</div>
          ) : error ? (
            <div className="announcement-error">
              <p>Sorry, we couldn't load our latest announcements.</p>
              <p>Please check back later!</p>
            </div>
          ) : announcements.length > 0 ? (
            <div className="announcement-card">
              <div className="announcement-header">
                <div className="announcement-tag-container">
                  <div 
                    className="announcement-tag" 
                    data-tag={announcements[currentAnnouncement].tag}
                  >
                    {announcements[currentAnnouncement].tag || 'Update'}
                  </div>
                  <div className="announcement-date">
                    {formatAnnouncementDate(announcements[currentAnnouncement].publish_start)}
                  </div>
                </div>
              </div>
              
              <h3 className="announcement-title">{announcements[currentAnnouncement].title}</h3>
              
              {/* Display summary as a subtitle if it exists and is different from body */}
              {announcements[currentAnnouncement].summary && 
               announcements[currentAnnouncement].summary !== announcements[currentAnnouncement].body && (
                <p className="announcement-summary">{announcements[currentAnnouncement].summary}</p>
              )}
              
              {/* Display the full body content */}
              <div className="announcement-body">
                {announcements[currentAnnouncement].body ? (
                  <div dangerouslySetInnerHTML={{ 
                    __html: announcements[currentAnnouncement].body.replace(/\n/g, '<br />') 
                  }} />
                ) : (
                  <p>{announcements[currentAnnouncement].summary}</p>
                )}
              </div>
              
              {/* Image display */}
              {announcements[currentAnnouncement].image_url && 
               announcements[currentAnnouncement].image_url !== '' && (
                <div className="announcement-image-container">
                  <img 
                    src={announcements[currentAnnouncement].image_url} 
                    alt={announcements[currentAnnouncement].title}
                    className="announcement-image" 
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="announcement-empty">
              <div className="announcement-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="120" height="120">
                  {/* Farm bulletin board icon */}
                  <path d="M448 64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h384c35.35 0 64-28.65 64-64V128C512 92.65 483.3 64 448 64z" fill="#a67c52" opacity="0.1"/>
                  <path d="M448 80H64C37.49 80 16 101.5 16 128v256c0 26.51 21.49 48 48 48h384c26.51 0 48-21.49 48-48V128C496 101.5 474.5 80 448 80zM64 112h384c8.837 0 16 7.163 16 16v32H48v-32C48 119.2 55.16 112 64 112zM448 400H64c-8.837 0-16-7.163-16-16V192h416v192C464 392.8 456.8 400 448 400z" fill="#a67c52"/>
                  {/* Empty note/paper in the middle */}
                  <path d="M120 240h272c4.418 0 8 3.582 8 8v112c0 4.418-3.582 8-8 8H120c-4.418 0-8-3.582-8-8V248c0-4.418 3.582-8 8-8z" fill="#f5f0e5"/>
                  {/* Notice pin */}
                  <circle cx="256" cy="232" r="12" fill="#a67c52"/>
                  {/* Decorative lines for paper */}
                  <path d="M140 280h232M140 320h232" stroke="#d6bf9d" strokeWidth="4" strokeLinecap="round"/>
                  {/* Wheat stalks for farm theme */}
                  <path d="M104 64c0 0-8-16-24-16s-24 16-24 16M80 64v48M56 96h48M80 120c0 0 8 16 24 16s24-16 24-16" stroke="#a67c52" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M424 64c0 0-8-16-24-16s-24 16-24 16M400 64v48M376 96h48M400 120c0 0 8 16 24 16s24-16 24-16" stroke="#a67c52" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No News from the Stand Today</h3>
                <p>We don't have any new updates to share right now, but the stand is still stocked with fresh finds.</p>
                <p>Check back soon for updates on new arrivals, special offerings, and community events at the farmstand!</p>
            </div>
          )}
          
          {/* Only show navigation dots if we have multiple announcements */}
          {!isLoading && !error && announcements.length > 1 && (
            <div className="announcement-nav">
              {announcements.map((_, index) => (
                <button 
                  key={index} 
                  className={`announcement-dot ${index === currentAnnouncement ? 'active' : ''}`}
                  onClick={() => setCurrentAnnouncement(index)}
                  aria-label={`Announcement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section - UPDATED with enhanced states and specific class names */}
      <section className="featured-section">
        <h2>This Season's Harvest</h2>
        <div className="landing-inventory-showcase">
          {isLoadingProducts ? (
            // Enhanced loading state for products
            <div className="landing-inventory-loading">
              <div className="loading-spinner"></div>
              <p>Harvesting fresh produce...</p>
            </div>
          ) : productError ? (
            // Enhanced error state for products
            <div className="landing-inventory-error">
              <div className="error-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#b25d5d" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12" y2="16" />
                </svg>
              </div>
              <h3>We're having trouble loading our products</h3>
              <p>Please check back soon as we refresh our inventory. Meanwhile, you can visit our farmstand in person!</p>
            </div>
          ) : seasonalProducts.length > 0 ? (
            // Successfully loaded products with enhanced quantity input
            seasonalProducts.map((product, index) => (
              <div className="landing-inventory-card" key={index}>
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="landing-inventory-image" 
                  onError={(e) => {
                    // Fallback image if product image fails to load
                    e.target.src = "https://images.unsplash.com/photo-1627464617543-e521e9fe618e?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80";
                    console.log(`Image failed to load for ${product.name}, using fallback`);
                  }}
                />
                <div className="landing-inventory-content">
                  <h3 className="landing-inventory-title">{product.name}</h3>
                  <p className="landing-inventory-description">{product.description}</p>
                  <div className="landing-inventory-price">${product.price.toFixed(2)} {product.unit && `/ ${product.unit}`}</div>
                  
                  {/* Quantity Input Mode */}
                  {quantityInputMode[product.id] ? (
                    <div className="quantity-input-container">
                      <div className="quantity-input-wrapper">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={quantityValues[product.id] === '' ? '' : (quantityValues[product.id] || 1)}
                          onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                          onBlur={() => handleQuantityBlur(product.id)}
                          onFocus={handleQuantityFocus}
                          className="quantity-input"
                          placeholder="1"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                        <span className="quantity-unit">{product.unit || 'ea'}</span>
                      </div>
                      
                      {/* Special Pricing Preview for Corn */}
                      {product.name && product.name.toLowerCase().includes('corn') && (quantityValues[product.id] >= 12) && (
                        <div className="special-pricing-preview">
                          {(() => {
                            const qty = quantityValues[product.id] || 1;
                            // Only show pricing preview if we have a valid number
                            if (qty === '' || qty < 1) return null;
                            
                            const pricing = calculateSpecialPricing(product, qty);
                            const dozens = Math.floor(qty / 12);
                            const remainder = qty % 12;
                            
                            return (
                              <div className="pricing-breakdown">
                                <div className="dozen-pricing">
                                  🌽 {dozens} dozen @ $8.00 = ${(dozens * 8).toFixed(2)}
                                </div>
                                {remainder > 0 && (
                                  <div className="remainder-pricing">
                                    + {remainder} ea @ ${product.price.toFixed(2)} = ${(remainder * product.price).toFixed(2)}
                                  </div>
                                )}
                                <div className="total-pricing">
                                  <strong>Total: ${pricing.totalPrice.toFixed(2)}</strong>
                                  <span className="savings"> (Save ${((qty * product.price) - pricing.totalPrice).toFixed(2)}!)</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      <div className="quantity-actions">
                        <button 
                          className="confirm-add-btn" 
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={product.status === 'Out of Stock'}
                        >
                          Add to Basket
                        </button>
                        <button 
                          className="cancel-quantity-btn"
                          onClick={(e) => handleCancelQuantityInput(product.id, e)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Regular Add to Cart Button */
                    <button 
                      className="landing-inventory-cta" 
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={product.status === 'Out of Stock'}
                    >
                      {product.status === 'Out of Stock' ? 'Out of Stock' : 'Add to Basket'}
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            // Enhanced empty state for products
            <div className="landing-inventory-empty">
              <div className="product-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="80" height="80" fill="none" stroke="#a67c52" strokeWidth="1">
                  <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20z" fill="#f5f0e5" strokeWidth="0.5" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" strokeLinecap="round" />
                  <path d="M9 9h.01M15 9h.01" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M3 7c0-3 2-5 5-5s3 2 3 5M21 7c0-3-2-5-5-5s-3 2-3 5" strokeDasharray="1,3" opacity="0.5" />
                  <path d="M8 18c-2-1-2-3-2-4M16 18c2-1 2-3 2-4" strokeDasharray="1,2" opacity="0.5" />
                </svg>
              </div>
              <h3>Taking a brief harvest break</h3>
              <p>We're currently between harvests or preparing new seasonal offerings.</p>
              <p>Check back soon for fresh picks from our fields, or visit us in person to see what's available today!</p>
              <button 
                className="cta-secondary visit-btn" 
                onClick={() => window.location.href = '/about#location'}
              >
                Find Our Location
              </button>
            </div>
          )}
        </div>
        {seasonalProducts.length > 0 && (
          <div className="view-all-container">
            <button className="view-all-btn" onClick={navigateToShop}>View All Products</button>
          </div>
        )}
      </section>

     {/* Subscription Section */}
      <section className="subscription-section">
        <div className="subscription-content">
          <h2>The Buck's Box</h2>
          <p>
            Get a curated box of seasonal produce delivered weekly or monthly. Experience the best of what our farm has to offer, right at your doorstep.
          </p>
          <ul className="subscription-benefits">
            <li>Always seasonal, always fresh</li>
            <li>Convenient delivery options</li>
            <li>Support sustainable farming</li>
            <li>Discover new varieties</li>
          </ul>
          <button className="cta-primary" onClick={navigateToSubscription}>Subscribe Now</button>
        </div>
        <div className="subscription-image">
          <img
            src={bucksBoxImage}
            alt="Buck's Box Subscription"
          />
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <h2>What Our Customers Say</h2>
        <div className="testimonial-carousel">
          <div className="testimonial-card">
            <p className="testimonial-quote">"{testimonials[currentTestimonial].quote}"</p>
            <p className="testimonial-author">- {testimonials[currentTestimonial].author}</p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <div className="newsletter-container">
          <h2>Stay Connected</h2>
          <p>
            Sign up for our newsletter to receive updates on seasonal offerings, special promotions, and farm events.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="newsletter-form-container">
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="newsletter-input" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button 
                type="submit" 
                className="newsletter-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
            
            {/* Message display */}
            {subscribeMessage && (
              <div className="newsletter-success">
                {subscribeMessage}
              </div>
            )}
            
            {subscribeError && (
              <div className="newsletter-error">
                {subscribeError}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Farm Story Section */}
      <section className="farm-story-section">
        <div className="farm-story-image">
          <img
            src={bucksStoryImage}
            alt="Our Farm Story"
          />
        </div>
        <div className="farm-story-content">
          <h2>Our Story</h2>
          <p>
            Nestled in Alsip, Illinois, Buck's Farmstand has been a family-run gathering spot for fresh, local flavors for over three generations. We partner with nearby farms for seasonal produce, raise our own hens for pasture-raised eggs, and craft small-batch apple sauce and artisan breads right here in town.
          </p>
          <p>
            Every item on our stand is a celebration of community, carefully curated to honor sustainable practices and the hard work of local farms. When you shop with us, you're supporting the land and neighbors who make Buck's Farmstand possible.
          </p>
          <button className="cta-secondary" onClick={navigateToStory}>
            Discover Our Roots
          </button>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="final-cta-section">
        <h2>Experience the Bucks Difference Today</h2>
        <p>
          From our farmstand to your table; taste the difference that care, quality, and tradition make.
        </p>
        <button className="cta-primary cta-large" onClick={navigateToShop}>Start Shopping</button>
      </section>

      {/* Enhanced Cart Notification to match shop page */}
      <div id="cart-notification" className="cart-notification">
        Item added to basket
      </div>

      {/* Cart notification styling */}
      <style>
        {`
          /* Enhanced Cart Notification to match shop page */
          .cart-notification {
            position: fixed;
            bottom: -100px;
            left: 50%;
            transform: translateX(-50%);
            background-color: #a67c52;
            color: white;
            padding: 1rem 2rem;
            border-radius: 50px;
            font-weight: 600;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
            opacity: 0;
            transition: all 0.5s ease;
            z-index: 1000;
            max-width: 90vw;
            text-align: center;
          }

          .cart-notification.show {
            bottom: 30px;
            opacity: 1;
          }

          .cart-notification.error {
            background-color: #f8d7da;
            color: #721c24;
            border: 1px solid #f5c6cb;
          }

          /* Quantity Input Styling for Landing Page */
          .quantity-input-container {
            display: flex;
            flex-direction: column;
            gap: 0.8rem;
            animation: slideInFromButton 0.4s ease-out;
            margin-top: 1rem;
          }

          .quantity-input-wrapper {
            display: flex;
            align-items: center;
            background-color: #f8f5f0;
            border: 2px solid #a67c52;
            border-radius: 50px;
            padding: 0.4rem 1rem;
            gap: 0.5rem;
            transition: all 0.3s ease;
          }

          .quantity-input-wrapper:focus-within {
            background-color: #fff;
            box-shadow: 0 0 0 3px rgba(166, 124, 82, 0.2);
          }

          .quantity-input {
            border: none;
            background: transparent;
            font-size: 1rem;
            font-weight: 600;
            color: #6b4c35;
            width: 60px;
            text-align: center;
            outline: none;
          }

          .quantity-input::-webkit-outer-spin-button,
          .quantity-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          .quantity-input[type=number] {
            -moz-appearance: textfield;
          }

          .quantity-unit {
            font-size: 0.9rem;
            color: #a67c52;
            font-weight: 600;
            white-space: nowrap;
          }

          .quantity-actions {
            display: flex;
            gap: 0.5rem;
          }

          .confirm-add-btn {
            flex: 1;
            background-color: #a67c52;
            color: white;
            border: none;
            border-radius: 50px;
            padding: 0.7rem 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 0.9rem;
          }

          .confirm-add-btn:hover:not(:disabled) {
            background-color: #8c6544;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(166, 124, 82, 0.3);
          }

          .confirm-add-btn:disabled {
            background-color: #cccccc;
            cursor: not-allowed;
          }

          .cancel-quantity-btn {
            background: transparent;
            color: #a67c52;
            border: 2px solid #a67c52;
            border-radius: 50px;
            padding: 0.7rem 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 0.9rem;
            white-space: nowrap;
          }

          .cancel-quantity-btn:hover {
            background-color: rgba(166, 124, 82, 0.1);
            transform: translateY(-2px);
          }

          /* Special Pricing Preview Styling */
          .special-pricing-preview {
            background-color: #e8f5e8;
            border: 1px solid #4caf50;
            border-radius: 8px;
            padding: 0.8rem;
            margin: 0.5rem 0;
            font-size: 0.85rem;
          }

          .pricing-breakdown {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
          }

          .dozen-pricing,
          .remainder-pricing {
            color: #2e7d32;
          }

          .total-pricing {
            border-top: 1px solid #4caf50;
            padding-top: 0.3rem;
            margin-top: 0.3rem;
            color: #1b5e20;
          }

          .savings {
            color: #388e3c;
            font-weight: 600;
          }

          /* Animation for smooth transition */
          @keyframes slideInFromButton {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          /* Loading and error states for announcements */
          .announcement-loading,
          .announcement-error,
          .announcement-empty {
            background-color: white;
            padding: 3rem;
            border-radius: 16px;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
            margin-bottom: 2rem;
            text-align: center;
            min-height: 200px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border-top: 4px solid #a67c52;
          }
          
          .announcement-error {
            border-top-color: #b25d5d;
          }
          
          /* Enhanced empty announcements state */
          .announcement-empty {
            padding: 3rem 2rem 2rem;
            background-color: #fcfaf5;
            border-top: 4px solid #d6bf9d;
            min-height: 300px;
          }
          
          .announcement-empty-icon {
            margin-bottom: 1rem;
            color: #a67c52;
          }
          
          .announcement-empty h3 {
            font-size: 1.8rem;
            margin-bottom: 1rem;
            color: #6b4c35;
          }
          
          .announcement-empty p {
            color: #6b5a44;
            max-width: 450px;
            margin: 0 auto 1rem;
          }
          
          .announcement-empty-image {
            margin-top: 1.5rem;
            max-width: 90%;
            border-radius: 12px;
            max-height: 180px;
            object-fit: cover;
            box-shadow: 0 15px 25px rgba(0, 0, 0, 0.1);
            transition: transform 0.4s ease;
          }
          
          .announcement-empty-image:hover {
            transform: scale(1.03);
          }
          
          .announcement-image {
            margin-top: 1.5rem;
            max-width: 100%;
            border-radius: 8px;
            max-height: 200px;
            object-fit: cover;
          }
          
          /* Enhanced product loading, error, and empty states - UPDATED with new class names */
          .landing-inventory-loading,
          .landing-inventory-error,
          .landing-inventory-empty {
            background-color: white;
            padding: 3rem;
            border-radius: 16px;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
            margin-bottom: 2rem;
            text-align: center;
            min-height: 200px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border-top: 4px solid #a67c52;
            width: 100%;
          }
          
          .landing-inventory-error {
            border-top-color: #b25d5d;
          }
          
          /* Updated showcase container */
          .landing-inventory-showcase {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin-bottom: 3rem;
          }
          
          /* Updated product card styles */
          .landing-inventory-card {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
            transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
            border-top: 4px solid #d6bf9d;
          }
          
          .landing-inventory-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
          }
          
          .landing-inventory-image {
            width: 100%;
            height: 200px;
            object-fit: cover;
            transition: transform 0.4s ease;
          }
          
          .landing-inventory-card:hover .landing-inventory-image {
            transform: scale(1.05);
          }
          
          .landing-inventory-content {
            padding: 1.5rem;
          }
          
          .landing-inventory-title {
            font-size: 1.4rem;
            margin-bottom: 0.5rem;
            color: #6b4c35;
            font-weight: 600;
          }
          
          .landing-inventory-description {
            color: #6b5a44;
            margin-bottom: 1rem;
            line-height: 1.5;
          }
          
          .landing-inventory-price {
            font-weight: 600;
            color: #a67c52;
            margin: 0.5rem 0 1rem;
            font-size: 1.1rem;
          }
          
          .landing-inventory-cta {
            background-color: #a67c52;
            color: white;
            border: none;
            padding: 0.8rem 1.5rem;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            width: 100%;
          }
          
          .landing-inventory-cta:hover {
            background-color: #8c6544;
            transform: translateY(-2px);
          }
          
          /* Loading spinner animation */
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .loading-spinner {
            border: 5px solid #f5f0e5;
            border-top: 5px solid #a67c52;
            border-radius: 50%;
            width: 50px;
            height: 50px;
            animation: spin 1.5s linear infinite;
            margin: 0 auto 1.5rem;
          }
          
          /* Enhanced empty state styling */
          .landing-inventory-empty {
            background-color: white;
            padding: 4rem 2rem;
            border-radius: 16px;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
            text-align: center;
            width: 100%;
            border-top: 4px solid #d6bf9d;
          }
          
          .product-empty-icon {
            margin-bottom: 1.5rem;
            display: flex;
            justify-content: center;
          }
          
          .landing-inventory-empty h3 {
            font-size: 1.8rem;
            margin-bottom: 1rem;
            color: #6b4c35;
          }
          
          .landing-inventory-empty p {
            color: #6b5a44;
            max-width: 450px;
            margin: 0 auto 1rem;
          }
          
          .visit-btn {
            margin-top: 1.5rem;
          }
          
          /* Error state styling */
          .landing-inventory-error {
            background-color: white;
            padding: 4rem 2rem;
            border-radius: 16px;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
            text-align: center;
            width: 100%;
            border-top: 4px solid #b25d5d;
          }
          
          .error-icon {
            margin-bottom: 1.5rem;
            display: flex;
            justify-content: center;
          }
          
          .landing-inventory-error h3 {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            color: #6b4c35;
          }
          
          /* Newsletter form styling */
          .newsletter-form-container {
            display: flex;
            flex-direction: column;
            width: 100%;
          }
          
          .newsletter-form {
            display: flex;
            margin-top: 2rem;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
            border-radius: 50px;
            overflow: hidden;
            background-color: white;
          }
          
          .newsletter-name-field {
            margin-top: 1rem;
            width: 100%;
          }
          
          .newsletter-name-field input {
            width: 100%;
            border-radius: 50px;
            padding: 1rem 1.5rem;
            font-size: 1rem;
            border: none;
            outline: none;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
          }
          
          .newsletter-success {
            margin-top: 1rem;
            padding: 0.8rem 1.5rem;
            background-color: rgba(255, 255, 255, 0.9);
            color: #5a8a44;
            border-radius: 50px;
            font-weight: 600;
            animation: fadeIn 0.5s ease;
          }
          
          .newsletter-error {
            margin-top: 1rem;
            padding: 0.8rem 1.5rem;
            background-color: rgba(255, 255, 255, 0.9);
            color: #a85d5d;
            border-radius: 50px;
            font-weight: 600;
            animation: fadeIn 0.5s ease;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          /* Responsive adjustments for mobile */
          @media (max-width: 768px) {
            .quantity-input-wrapper {
              padding: 0.3rem 0.8rem;
            }
            
            .quantity-input {
              width: 50px;
              font-size: 0.9rem;
            }
            
            .quantity-actions {
              flex-direction: column;
            }
            
            .confirm-add-btn,
            .cancel-quantity-btn {
              padding: 0.6rem 0.8rem;
              font-size: 0.85rem;
            }

            .newsletter-form {
              flex-direction: column;
              border-radius: 12px;
            }
            
            .newsletter-input {
              width: 100%;
              padding: 1rem;
            }
            
            .newsletter-btn {
              width: 100%;
              border-radius: 0 0 12px 12px;
            }
            
            .newsletter-name-field input {
              border-radius: 12px;
            }
            
            .newsletter-success,
            .newsletter-error {
              border-radius: 12px;
            }
            
            .announcement-empty h3 {
              font-size: 1.5rem;
            }
            
            .announcement-empty-icon svg {
              width: 90px;
              height: 90px;
            }
            
            .landing-inventory-showcase {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }
            
            .landing-inventory-empty h3 {
              font-size: 1.5rem;
            }
          }
        `}
      </style>

      {/* Enhanced Hero Section Styles */}
      <style>
        {`
          /* Enhanced Hero Section with Ogilvy principles and Apple aesthetics */

          /* Hero Section Base - refined gradients and animations */
          .hero-section {
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 90vh;
            padding: 8rem 6rem;
            background: linear-gradient(135deg, rgba(255, 252, 245, 0.97), rgba(245, 240, 225, 0.93));
            position: relative;
            overflow: hidden;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 1.2s ease, transform 1.2s ease;
          }

          /* Create subtle organic patterns for texture like Apple designs */
          .hero-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23a67c52' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
            pointer-events: none;
          }

          .hero-section.visible {
            opacity: 1;
            transform: translateY(0);
          }

          /* Headline refinement - Ogilvy emphasized strong headlines */
          .hero-content h1 {
            font-size: 3.8rem;
            line-height: 1.2;
            letter-spacing: -0.02em;
            background: linear-gradient(to right, #6b4c35, #a67c52);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 1.5rem;
            position: relative;
            font-weight: 800;
            max-width: 600px;
          }

          /* Subtle underline for headline - Apple-like understated details */
          .hero-content h1::after {
            content: "";
            position: absolute;
            bottom: -10px;
            left: 0;
            width: 100px;
            height: 3px;
            background: #d6bf9d;
            border-radius: 3px;
          }

          /* Subtitle enhancement - Apple's clear, precise language */
          .hero-subtitle {
            font-size: 1.5rem;
            line-height: 1.6;
            color: #6b5a44;
            margin-bottom: 2.5rem;
            font-weight: 300;
            max-width: 540px;
          }

          /* Content area refinement */
          .hero-content {
            flex: 1;
            padding-right: 5rem;
            position: relative;
            z-index: 2;
          }

          /* Image container refinement */
          .hero-image-container {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            z-index: 1;
          }

          /* Apple-like image treatment */
          .hero-image {
            max-width: 100%;
            border-radius: 24px;
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
            transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
            transform-origin: center bottom;
            margin-bottom: 30px;
          }

          .hero-image:hover {
            transform: scale(1.03) translateY(-10px);
            box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2);
          }

          /* Ogilvy-inspired CTA section with clarity and focus */
          .hero-cta-container {
            margin-top: 3rem;
            display: flex;
            align-items: center;
          }

          /* Primary CTA enhancement with Apple-like simplicity */
          .cta-primary {
            background-color: #a67c52;
            color: white;
            font-weight: 600;
            padding: 1rem 2.2rem;
            border-radius: 50px;
            font-size: 1.1rem;
            letter-spacing: 0.02em;
            transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
            position: relative;
            overflow: hidden;
            z-index: 1;
          }

          .cta-primary:hover {
            background-color: #8c6544;
            transform: translateY(-3px) scale(1.02);
            box-shadow: 0 10px 25px rgba(138, 97, 66, 0.25);
          }

          /* Ogilvy-inspired secondary action with less prominence */
          .cta-secondary {
            background-color: transparent;
            color: #a67c52;
            border: 2px solid #a67c52;
            margin-left: 1.5rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
          }

          .cta-secondary:hover {
            background-color: rgba(166, 124, 82, 0.08);
            transform: translateY(-3px);
            box-shadow: 0 10px 25px rgba(138, 97, 66, 0.15);
          }

          /* Add subtle decoration elements for visual interest */
          .hero-content::before {
            content: "";
            position: absolute;
            top: -80px;
            left: -60px;
            width: 150px;
            height: 150px;
            background: radial-gradient(circle, rgba(166, 124, 82, 0.08) 0%, rgba(166, 124, 82, 0) 70%);
            border-radius: 50%;
            z-index: -1;
          }

          /* Hero testimonial preview */
          .hero-testimonial-preview {
            margin-top: 2rem;
            display: flex;
            flex-direction: column;
            max-width: 350px;
            opacity: 0.8;
            transition: all 0.3s ease;
            border-left: 2px solid rgba(166, 124, 82, 0.3);
            padding-left: 1rem;
            margin-left: 0.5rem;
          }
          
          .hero-testimonial-preview:hover {
            opacity: 1;
          }
          
          .rating-stars {
            color: #a67c52;
            letter-spacing: 2px;
            margin-bottom: 0.3rem;
            font-size: 0.9rem;
          }
          
          .hero-testimonial-preview p {
            font-style: italic;
            font-size: 0.95rem;
            margin: 0;
            color: #6b5a44;
          }
          
          .hero-decoration-circle {
            position: absolute;
            width: 250px;
            height: 250px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(214, 191, 157, 0.1) 0%, rgba(214, 191, 157, 0) 70%);
            top: -50px;
            right: -100px;
            z-index: -1;
            animation: float 6s ease-in-out infinite;
          }
          
          .hero-image-badge {
            position: absolute;
            bottom: 30px;
            right: -15px;
            background: #a67c52;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: white;
            font-weight: 700;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
            transform: rotate(-8deg);
            animation: float 5s ease-in-out infinite;
          }
          
          .hero-image-badge span:first-child {
            font-size: 1.4rem;
            line-height: 1;
          }
          
          .hero-image-badge span:last-child {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          
          /* Subtle floating animation for decorative elements - Apple-like attention to motion */
          @keyframes float {
            0% { transform: translateY(0px) rotate(-8deg); }
            50% { transform: translateY(-15px) rotate(-8deg); }
            100% { transform: translateY(0px) rotate(-8deg); }
          }

          /* Media queries for responsive design */
          @media (max-width: 1280px) {
            .hero-section {
              padding: 6rem 4rem;
            }
            
            .hero-content h1 {
              font-size: 3.2rem;
            }
          }

          @media (max-width: 1024px) {
            .hero-section {
              flex-direction: column;
              padding: 5rem 3rem;
              text-align: center;
            }
            
            .hero-content {
              padding-right: 0;
              margin-bottom: 3rem;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            
            .hero-content h1 {
              font-size: 3rem;
            }
            
            .hero-content h1::after {
              left: 50%;
              transform: translateX(-50%);
            }
            
            .hero-subtitle {
              max-width: 100%;
            }
            
            .hero-testimonial-preview {
              margin: 2rem auto 0;
              border-left: none;
              border-top: 2px solid rgba(166, 124, 82, 0.3);
              padding-left: 0;
              padding-top: 1rem;
              text-align: center;
            }
            
            .hero-image-badge {
              right: 50%;
              transform: translateX(80px) rotate(-8deg);
            }
          }

          @media (max-width: 768px) {
            .hero-section {
              padding: 4rem 2rem;
            }
            
            .hero-content h1 {
              font-size: 2.5rem;
            }
            
            .hero-subtitle {
              font-size: 1.2rem;
            }
            
            .hero-cta-container {
              flex-direction: column;
              width: 100%;
            }
            
            .cta-primary, .cta-secondary {
              width: 100%;
              margin: 0.5rem 0;
            }
            
            .cta-secondary {
              margin-left: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default BucksLanding;